import { toast } from "react-toastify";
import { useEffect, useMemo, useState } from "react";
import { SubmitButton as Button } from "~/components/form/submit-button";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import {
  getAllAgentDetails,
  getAllAgentGroupDetails,
  assignTask
} from "~/services/supervisor.services";
import { tr } from "date-fns/locale";

interface IModalProps {
  isOpen: boolean;
  onClose: (shouldRefresh?: boolean) => void;
  taskIds?: string[]; // Optional prop to receive selected task IDs
}

type ListItem = {
  userid?: string;
  name?: string;
  Groupid?: string;
};

type AgentDetailsResponse = {
  status: boolean;
  data?: { data: ListItem[] };
  error?: any;
};

type AssignTaskResponse = {
  status: boolean;
  data?: { data?: string, massage?: string, status?: number };
};

const AllocationModal = ({ isOpen, onClose, taskIds }: IModalProps) => {
  const [agentList, setAgentList] = useState<ListItem[]>([]);
  const [groupList, setGroupList] = useState<ListItem[]>([]);
  const [selectedAgent, setSelectedAgent] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitResult, setSubmitResult] = useState<string>("");

  const isFormValid = Boolean(selectedAgent || selectedGroup);

  // Fetch agents and groups on mount
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [agentRes, groupRes] = await Promise.all([
          getAllAgentDetails() as Promise<AgentDetailsResponse>,
          getAllAgentGroupDetails() as Promise<AgentDetailsResponse>,
        ]);
        if (agentRes.status) setAgentList(agentRes.data?.data ?? []);
        if (groupRes.status) setGroupList(groupRes.data?.data ?? []);
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) resetSelections();
  }, [isOpen]);

  const resetSelections = () => {
    setSelectedAgent("");
    setSelectedGroup("");
    setIsSubmitted(false);
    setIsLoading(false);
  };

  const showInfoToast = () => {
    toast.warn(
      `Note:\n1. Your action will override the current auto-allocation rules.\n2. You can allocate tasks to either an agent or a group — only one can be selected.`,
      {
        position: "top-right",
        autoClose: 5000,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: { whiteSpace: "pre-line" },
      }
    );
  };

  const arrayToStringOrNull = (arr: any[]) => Array.isArray(arr) && arr.length ? arr.join(',') : null;

  const handleAllocate = async () => {
    if (!isFormValid) return;
    setIsLoading(true);
    try {
      const response = await assignTask({
        "userid": selectedAgent || null,
        "groupid": selectedGroup || null,
        "taskids": arrayToStringOrNull([taskIds]) // Convert array to comma-separated string or null
      }) as AssignTaskResponse;
      // await assignTask({});
      const { data } = response;
      if (response.status) {
        setSubmitResult(data?.data || "Tasks allocated successfully!");
        setIsSubmitted(true);
      } else {
        toast.error(data?.data || "Allocation failed. Please try again.");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex justify-center items-start z-50 pt-14">
      <div className="fixed inset-0 bg-gray-800 opacity-50" />
      <div className="bg-white rounded-2xl p-4 z-50 h-fit w-full max-w-3xl">
        {isSubmitted ? (
          <div className="text-center">
            <p className="font-semibold text-gray-900 max-h-[300px] overflow-auto">
              <div dangerouslySetInnerHTML={{ __html: submitResult }} />
            </p>
            <div className="flex justify-right float-right mt-2">
              <Button
                role="button"
                variant="secondary"
                radius="full"
                size="medium"
                onClick={() => onClose(true)}
              >
                Close
              </Button>
            </div>
          </div>
        ) : (
          <>
            <h1 className="text-center text-2xl font-semibold text-gray-900 mb-4">
              Manual Allocation{" "}
              <ExclamationTriangleIcon
                onClick={showInfoToast}
                className="cursor-pointer h-8 w-8 inline text-yellow-500"
              />
            </h1>

            <div className="flex grid-cols-2 justify-around gap-2">
              {/* Allocate to Agent */}
              <div className="mb-4 w-1/2">
                <label
                  className="block text-sm font-medium text-gray-700"
                  htmlFor="allocate-agent"
                >
                  Allocate to Agent
                </label>
                <select
                  id="allocate-agent"
                  className="mt-1 w-full border rounded-md px-3 py-2"
                  value={selectedAgent}
                  disabled={Boolean(selectedGroup)}
                  onChange={(e) => setSelectedAgent(e.target.value)}
                >
                  <option value="">Select an Agent</option>
                  {agentList.map((item) => (
                    <option key={item.userid} value={item.userid}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Allocate to Group */}
              <div className="mb-4 w-1/2">
                <label
                  className="block text-sm font-medium text-gray-700"
                  htmlFor="allocate-group"
                >
                  Allocate to Group
                </label>
                <select
                  id="allocate-group"
                  className="mt-1 w-full border rounded-md px-3 py-2"
                  value={selectedGroup}
                  disabled={Boolean(selectedAgent)}
                  onChange={(e) => setSelectedGroup(e.target.value)}
                >
                  <option value="">Select a Group</option>
                  {groupList.map((item) => (
                    <option key={item.Groupid} value={item.Groupid}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex flex-row-reverse gap-2">
              <Button
                role="button"
                radius="full"
                size="medium"
                type="button"
                onClick={handleAllocate}
                disabled={!isFormValid || isLoading}
              >
                {isLoading ? (
                  <span className="animate-spin border-2 border-orange-400 border-t-transparent w-4 h-4 rounded-full inline-block" />
                ) : (
                  "Allocate"
                )}
              </Button>
              <Button
                role="button"
                variant="secondary"
                radius="full"
                size="medium"
                onClick={resetSelections}
              >
                Reset Selection
              </Button>
              <Button
                role="button"
                variant="secondary"
                radius="full"
                size="medium"
                onClick={() => onClose(false)}
              >
                Cancel
              </Button>
              {/* <div>4 tasks selected for assignment.</div> */}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AllocationModal;